<script setup lang="ts">
import VulnerabilitySeverity from '@/_core/entities/vulnerability/ui/VulnerabilitySeverity/VulnerabilitySeverity.vue';
import SeverityCountBadge from '@/_core/entities/vulnerability/ui/SeverityCountBadge/SeverityCountBadge.vue';
import SeverityDistribution from '@/_core/entities/report/ui/SeverityDistribution/SeverityDistribution.vue';
import type { TComplianceRequirement, TComplianceRequirementItem } from '@/_core/entities/report/models/types';

const props = defineProps<{
  requirement: TComplianceRequirement;
  expanded: boolean;
  selectedRequirementId?: string;
  selectedItemId?: string;
}>();

const emit = defineEmits<{
  (e: 'toggle'): void;
  (e: 'viewRequirement'): void;
  (e: 'viewItem', item: TComplianceRequirementItem): void;
}>();

const ROW_GRID =
  'tw:grid-cols-[1.5rem_minmax(10rem,1fr)_4rem_minmax(10rem,22rem)_6rem] ' +
  'tw:max-[890px]:grid-cols-[1.5rem_minmax(0,1fr)_7rem_9rem] ' +
  'tw:max-[576px]:grid-cols-[1.5rem_minmax(0,1fr)_auto_auto]';

const ITEM_ROW_GRID =
  'tw:grid-cols-[1.5rem_minmax(10rem,1fr)_4rem_minmax(10rem,22rem)_6rem] ' +
  'tw:max-[890px]:grid-cols-[minmax(0,1fr)_7rem_9rem] ' +
  'tw:max-[576px]:grid-cols-[minmax(0,1fr)_auto_auto]';

const TREE_LINE =
  'tw:relative tw:max-[890px]:before:hidden tw:max-[890px]:after:hidden ' +
  "tw:before:content-[''] tw:before:absolute tw:before:left-[34px] tw:before:top-0 tw:before:bottom-0 tw:before:w-px tw:before:bg-[#e4e8ee] " +
  "tw:after:content-[''] tw:after:absolute tw:after:left-[34px] tw:after:top-1/2 tw:after:w-4 tw:after:h-px tw:after:bg-[#e4e8ee]";

const TREE_LINE_LAST =
  'tw:before:!bottom-1/2 tw:before:!w-4 tw:before:!bg-transparent tw:before:border-l tw:before:border-b tw:before:border-[#e4e8ee] tw:before:rounded-bl-[10px] tw:after:hidden';
</script>

<template>
  <div class="tw:border-t tw:border-[#E4E8EE] tw:first:border-t-0">
    <!-- Requirement header -->
    <div
      :class="[
        'tw:grid tw:items-center tw:gap-6 tw:p-6 tw:max-[399px]:px-0 tw:typography-small tw:cursor-pointer tw:hover:bg-black/2 tw:transition-colors tw:duration-75',
        ROW_GRID,
        selectedRequirementId === requirement.id && !selectedItemId ? 'tw:bg-blue-bg/60' : '',
      ]"
      @click="$emit('toggle')"
    >
      <button
        type="button"
        class="tw:size-6 tw:grid tw:place-items-center tw:rounded"
        @click.stop="$emit('toggle')"
      >
        <span
          class="tw:inline-block tw:transition-transform tw:text-[#949DB8]"
          :class="{ 'tw:rotate-90': expanded }"
        >▸</span>
      </button>

      <div class="tw:min-w-0 tw:flex tw:items-center tw:gap-2 tw:pl-6 tw:max-[890px]:pl-0">
        <span class="tw:truncate tw:font-medium tw:text-[#021631]">{{ requirement.name }}</span>
        <a
          v-if="requirement.externalUrl"
          :href="requirement.externalUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="tw:text-[#5C6A7A] tw:typography-small tw:shrink-0 tw:font-bold tw:no-underline tw:hover:underline tw:transition-colors tw:duration-75"
          @click.stop
        >↗</a>
      </div>

      <div class="tw:typography-small tw:text-right tw:max-[890px]:hidden">
        {{ requirement.findings }}
      </div>

      <div class="tw:max-[890px]:flex tw:max-[890px]:justify-center tw:max-[890px]:items-center tw:max-[576px]:hidden">
        <SeverityDistribution :severity="requirement.severity" />
      </div>

      <div class="tw:justify-self-end tw:flex tw:items-center tw:gap-3 tw:whitespace-nowrap">
        <SeverityDistribution class="tw:hidden tw:max-[576px]:block" :severity="requirement.severity" :donut-size="32" />
        <button
          type="button"
          class="tw:typography-small tw:text-main-blue tw:hover:underline"
          @click.stop="$emit('viewRequirement')"
        >View findings</button>
      </div>
    </div>

    <!-- Expanded items -->
    <div v-if="expanded" class="tw:flex tw:flex-col tw:typography-small tw:pb-2">
      <template v-for="(item, idx) in requirement.items">
        <div
          :key="item.id"
          :class="[
            'tw:grid tw:items-center tw:gap-6 tw:p-6 tw:max-[399px]:px-0',
            ITEM_ROW_GRID,
            TREE_LINE,
            idx === requirement.items.length - 1 ? TREE_LINE_LAST : '',
            selectedItemId === item.id ? 'tw:bg-blue-bg/60' : '',
          ]"
        >
          <div class="tw:max-[890px]:hidden"></div>

          <div class="tw:min-w-0 tw:flex tw:flex-col tw:gap-2 tw:pl-6 tw:max-[890px]:pl-0">
            <div class="tw:flex tw:items-center tw:flex-wrap tw:gap-3">
              <a
                v-if="item.link"
                :href="item.link"
                target="_blank"
                rel="noopener noreferrer"
                class="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-3 tw:py-1 tw:rounded-full tw:bg-[#F5F7FA] tw:text-[#5C6A7A] tw:text-xs tw:max-w-full tw:no-underline tw:hover:underline"
                @click.stop
              >
                <span class="tw:truncate">{{ item.itemId }}</span>
                <span class="tw:text-[#5C6A7A] tw:font-bold tw:shrink-0">↗</span>
              </a>
              <span
                v-else
                class="tw:inline-flex tw:items-center tw:gap-1 tw:px-3 tw:py-1 tw:rounded-full tw:bg-[#F5F7FA] tw:text-[#5C6A7A] tw:text-xs tw:max-w-full"
              >
                <span class="tw:truncate">{{ item.itemId }}</span>
              </span>
            </div>
            <div class="tw:hidden tw:max-[576px]:flex tw:flex-wrap tw:items-center tw:gap-2">
              <SeverityCountBadge severity="high" :count="item.severity.high" />
              <SeverityCountBadge severity="medium" :count="item.severity.medium" />
              <SeverityCountBadge severity="low" :count="item.severity.low" />
            </div>
          </div>

          <div class="tw:typography-small tw:text-[#5C6A7A] tw:text-right tw:max-[890px]:hidden">
            {{ item.findings }}
          </div>

          <div class="tw:max-[890px]:flex tw:max-[890px]:justify-center tw:max-[890px]:items-center">
            <div class="tw:flex tw:items-center tw:flex-wrap tw:justify-around tw:gap-2.5 tw:typography-small tw:max-[890px]:hidden">
              <VulnerabilitySeverity severity="high" :postfix="String(item.severity.high)" />
              <VulnerabilitySeverity severity="medium" :postfix="String(item.severity.medium)" />
              <VulnerabilitySeverity severity="low" :postfix="String(item.severity.low)" />
            </div>
            <div class="tw:hidden tw:max-[890px]:flex tw:max-[576px]:hidden tw:items-center tw:justify-center tw:w-full tw:gap-2">
              <SeverityCountBadge severity="high" :count="item.severity.high" />
              <SeverityCountBadge severity="medium" :count="item.severity.medium" />
              <SeverityCountBadge severity="low" :count="item.severity.low" />
            </div>
          </div>

          <button
            type="button"
            class="tw:typography-small tw:text-main-blue tw:justify-self-end tw:hover:underline tw:inline-flex tw:items-center tw:gap-2 tw:whitespace-nowrap"
            @click="$emit('viewItem', item)"
          >
            <span class="tw:hidden tw:max-[890px]:inline-flex tw:items-center tw:justify-center tw:px-2 tw:py-0.5 tw:rounded-full tw:bg-blue-bg tw:text-main-blue tw:text-xs">
              {{ item.findings }}
            </span>
            View findings
          </button>
        </div>
        <div
          v-if="idx !== requirement.items.length - 1"
          :key="`div-${item.id}`"
          class="tw:h-px tw:bg-[#E4E8EE] tw:ml-14 tw:max-[890px]:ml-0"
        />
      </template>
    </div>
  </div>
</template>
